import { Elements } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getMe } from '@/features/auth/api/auth.api';
import {
  balanceKeys,
  getStripePromise,
  StripePaymentForm,
  useCreatePaymentIntent,
  useStripeConfigured,
} from '@/features/payments';
import { cn } from '@/lib/utils';
import { getAuthState, updateUser } from '@/stores/auth.store';
import { settingsKeys } from '../api/settings';

interface AddFundsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PRESET_AMOUNTS = [5, 10, 25];
const MIN_AMOUNT_POUNDS = 5;
const MAX_AMOUNT_POUNDS = 10000; // £10,000 max (matches backend validation)
const INITIAL_POLL_INTERVAL_MS = 500;
const MAX_POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 15;

type SelectionType = number | 'custom';
type ModalStep = 'amount-selection' | 'payment' | 'processing' | 'pending-confirmation' | 'success';

export function AddFundsModal({ open, onOpenChange }: AddFundsModalProps) {
  const [selected, setSelected] = useState<SelectionType | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [step, setStep] = useState<ModalStep>('amount-selection');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const stripeConfigured = useStripeConfigured();
  const { mutateAsync: createPaymentIntent, isPending: isCreatingIntent } =
    useCreatePaymentIntent();

  const effectiveAmount =
    selected === 'custom' ? (customAmount ? parseFloat(customAmount) : null) : selected;

  // Convert pounds to pence
  const amountPence = effectiveAmount ? Math.round(effectiveAmount * 100) : null;

  function handlePresetClick(amount: number) {
    setSelected(amount);
    setCustomAmount('');
  }

  function handleCustomClick() {
    setSelected('custom');
  }

  function handleCustomAmountChange(value: string) {
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setCustomAmount(value);
    }
  }

  async function handleProceed() {
    if (!amountPence) return;

    setError(null);

    if (!stripeConfigured) {
      setError('Payment system is not configured. Please contact support.');
      return;
    }

    try {
      const result = await createPaymentIntent(amountPence);
      setClientSecret(result.clientSecret);
      setStep('payment');
    } catch (err) {
      console.error('Failed to create payment intent:', err);
      setError(err instanceof Error ? err.message : 'Failed to create payment. Please try again.');
    }
  }

  async function handlePaymentSuccess() {
    setStep('processing');

    // Get the initial balance to compare against
    const initialBalance = parseFloat(String(getAuthState().user?.balance ?? 0));
    let attempts = 0;
    let interval = INITIAL_POLL_INTERVAL_MS;

    const pollForBalanceUpdate = async (): Promise<boolean> => {
      try {
        const { user } = await getMe();
        const newBalance = parseFloat(String(user.balance ?? 0));

        // Check if balance has increased
        if (newBalance > initialBalance) {
          updateUser(user);
          queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
          return true;
        }
      } catch {
        // Ignore errors during polling, will retry
      }
      return false;
    };

    // Poll with exponential backoff until balance updates or max attempts reached
    const poll = async () => {
      while (attempts < MAX_POLL_ATTEMPTS) {
        attempts++;
        const updated = await pollForBalanceUpdate();
        if (updated) {
          setStep('success');
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, interval));
        // Exponential backoff: increase interval by 1.5x, up to max
        interval = Math.min(interval * 1.5, MAX_POLL_INTERVAL_MS);
      }

      // Max attempts reached - show pending confirmation (balance will update shortly)
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
      setStep('pending-confirmation');
    };

    poll();
  }

  function handlePaymentCancel() {
    setStep('amount-selection');
    setClientSecret(null);
  }

  async function handleClose() {
    // If we were in a payment-related state, refresh user data to ensure balance is current
    if (step === 'pending-confirmation' || step === 'success' || step === 'processing') {
      try {
        const { user } = await getMe();
        updateUser(user);
        // Invalidate all balance-related queries so payment modals see updated balance
        queryClient.invalidateQueries({ queryKey: balanceKeys.all });
        queryClient.invalidateQueries({ queryKey: settingsKeys.profile() });
      } catch {
        // Ignore errors - user can manually refresh if needed
      }
    }

    setSelected(null);
    setCustomAmount('');
    setStep('amount-selection');
    setClientSecret(null);
    setError(null);
    onOpenChange(false);
  }

  const isValid =
    amountPence && amountPence >= MIN_AMOUNT_POUNDS * 100 && amountPence <= MAX_AMOUNT_POUNDS * 100;

  // Stripe appearance to match app styling
  const stripeAppearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#131313',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {step === 'success' ? 'Payment Successful' : 'Add Funds'}
          </DialogTitle>
        </DialogHeader>

        {step === 'amount-selection' && (
          <>
            <p className="text-sm text-gray-600">
              You can purchase Sanctions Screening and Anti Money Laundering Investigative Reports
              through your account balance. Select the amount below that you would like to add to
              your balance to continue to make purchases. Minimum deposit is £{MIN_AMOUNT_POUNDS},
              maximum £{MAX_AMOUNT_POUNDS.toLocaleString()}.
            </p>

            <div className="space-y-3 py-2">
              {/* Preset amounts - stacked vertically */}
              {PRESET_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  type="button"
                  variant="outline"
                  className={cn(
                    'h-12 w-full text-base font-normal',
                    selected === amount &&
                      'border-[#131313] bg-[#131313] !text-white hover:bg-[#131313]/90 hover:!text-white',
                  )}
                  onClick={() => handlePresetClick(amount)}
                >
                  £{amount}
                </Button>
              ))}

              {/* Custom Amount button */}
              <Button
                type="button"
                variant="outline"
                className={cn(
                  'h-12 w-full text-base font-normal',
                  selected === 'custom' &&
                    'border-[#131313] bg-[#131313] !text-white hover:bg-[#131313]/90 hover:!text-white',
                )}
                onClick={handleCustomClick}
              >
                Custom Amount
              </Button>

              {/* Custom amount input - shown when custom is selected */}
              {selected === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="customAmount" className="text-sm font-medium">
                    Custom Amount
                  </Label>
                  <Input
                    id="customAmount"
                    type="text"
                    inputMode="decimal"
                    placeholder="£50"
                    value={customAmount ? `£${customAmount}` : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace('£', '');
                      handleCustomAmountChange(value);
                    }}
                    className="h-12"
                  />
                  {customAmount && parseFloat(customAmount) < MIN_AMOUNT_POUNDS && (
                    <p className="text-sm text-red-500">Minimum deposit is £{MIN_AMOUNT_POUNDS}</p>
                  )}
                  {customAmount && parseFloat(customAmount) > MAX_AMOUNT_POUNDS && (
                    <p className="text-sm text-red-500">
                      Maximum deposit is £{MAX_AMOUNT_POUNDS.toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}

            <Button
              type="button"
              disabled={!isValid || isCreatingIntent}
              onClick={handleProceed}
              className="h-12 w-full bg-[#131313] text-base font-medium text-white hover:bg-[#131313]/90"
            >
              {isCreatingIntent ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Preparing Payment...
                </>
              ) : (
                'Continue to Payment'
              )}
            </Button>
          </>
        )}

        {step === 'payment' && clientSecret && amountPence && (
          <Elements
            stripe={getStripePromise()}
            options={{
              clientSecret,
              appearance: stripeAppearance,
            }}
          >
            <div className="py-2">
              <p className="mb-4 text-sm text-gray-600">
                You are adding <strong>£{(amountPence / 100).toFixed(2)}</strong> to your account
                balance.
              </p>
              <StripePaymentForm
                amount={amountPence}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          </Elements>
        )}

        {step === 'processing' && (
          <div className="py-8 text-center">
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-gray-400" />
            <p className="mt-4 text-sm text-gray-600">Processing your payment...</p>
            <p className="mt-1 text-xs text-gray-400">This may take a few seconds</p>
          </div>
        )}

        {step === 'pending-confirmation' && (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
              <CheckCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <p className="mb-2 text-lg font-medium">Payment Received</p>
            <p className="mb-6 text-sm text-gray-600">
              Your payment has been received. Your balance will be updated shortly.
            </p>
            <Button
              type="button"
              onClick={handleClose}
              className="h-12 w-full bg-[#131313] text-base font-medium text-white hover:bg-[#131313]/90"
            >
              Done
            </Button>
          </div>
        )}

        {step === 'success' && (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="mb-2 text-lg font-medium">
              £{effectiveAmount?.toFixed(2)} has been added to your account
            </p>
            <p className="mb-6 text-sm text-gray-600">
              Your balance has been updated and is ready to use.
            </p>
            <Button
              type="button"
              onClick={handleClose}
              className="h-12 w-full bg-[#131313] text-base font-medium text-white hover:bg-[#131313]/90"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
